class ShoppableLookbook extends Theme.Component {
  static #sections = new Map();

  #request = 0;

  connectedCallback() {
    super.connectedCallback();

    this.defineElements({
      container: '[data-element="scroll-container"]',
      slider: '[data-element="slider"]',
      previous: '[data-element="previous"]',
      next: '[data-element="next"]'
    });

    this.defineElementCollections({
      images: '[data-elements="images"]',
      swatches: '[data-elements="swatches"]'
    });

    this.addDirectEvent("scroll", this.elements.container, this.sync);
    this.addDirectEvent("resize", window, this.sync);
    this.addEvent("keydown", this.elements.container, this.onKeyDown);

    this.elements.swatches.forEach((swatch) => {
      this.addEvent("pointerover", swatch, (event) => {
        if (!swatch.contains(event.relatedTarget)) {
          this.prefetch(swatch);
        }
      });
      this.addEvent("focusin", swatch, () => this.prefetch(swatch));
    });
    this.sync();
  }

  sync() {
    const container = this.elements.container;
    const box = container.getBoundingClientRect();
    const center = box.left + container.clientWidth / 2;

    let nearest = 0;
    let distance = Infinity;

    this.elements.images.forEach((image, index) => {
      const image_box = image.getBoundingClientRect();
      const image_distance = Math.abs(image_box.left + image_box.width / 2 - center);

      if (image_distance < distance) {
        nearest = index;
        distance = image_distance;
      }
    });

    this.index = nearest;

    this.elements.images.forEach((image, index) => {
      image.inert = index !== nearest;
      image.setAttribute("aria-hidden", String(index !== nearest));
    });

    const max = container.scrollWidth - container.clientWidth;

    if (this.elements.previous) {
      this.elements.previous.disabled = container.scrollLeft <= 1;
      this.elements.next.disabled = container.scrollLeft >= max - 1;
    }
  }

  show(index) {
    const images = this.elements.images;
    const target = images[Math.max(0, Math.min(index, images.length - 1))];
    if (!target) {
      return;
    }

    const container = this.elements.container;
    const left = container.scrollLeft + target.getBoundingClientRect().left - container.getBoundingClientRect().left;

    this.elements.slider.animateScrollTo(left);
  }

  previous() {
    this.show(this.index - 1);
  }

  next() {
    this.show(this.index + 1);
  }

  onKeyDown(event) {
    if (event.target !== this.elements.container) {
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      this.show(this.index + (event.key === "ArrowRight" ? 1 : -1));
    }
  }

  prefetch(element) {
    if (!element.dataset.url) {
      return;
    }

    this.load(element.dataset.url)
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const images = doc.querySelectorAll(".lookbook-product-image, .lookbook-media:first-child .lookbook-image");

        images.forEach((image) => {
          const preload = new Image();
          preload.sizes = image.sizes;
          preload.srcset = image.srcset;
          preload.src = image.src;
        });
      })
      .catch(() => {});
  }

  load(variantUrl) {
    const url = new URL(variantUrl, window.location.origin);
    url.searchParams.set("section_id", this.dataset.sectionId);

    if (!ShoppableLookbook.#sections.has(url.href)) {
      const request = fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Unable to load shoppable lookbook.");
          }

          return response.text();
        })
        .catch((error) => {
          ShoppableLookbook.#sections.delete(url.href);
          throw error;
        });

      ShoppableLookbook.#sections.set(url.href, request);
    }

    return ShoppableLookbook.#sections.get(url.href);
  }

  async render(detail, element) {
    if (!element.dataset.url) {
      return;
    }

    const request = ++this.#request;
    const index = this.elements.swatches.indexOf(element);
    this.setAttribute("aria-busy", "true");

    try {
      const html = await this.load(element.dataset.url);

      if (request !== this.#request || !this.isConnected) {
        return;
      }

      const doc = new DOMParser().parseFromString(html, "text/html");
      const lookbook = doc.querySelector("component-shoppable-lookbook");

      if (!lookbook) {
        throw new Error("Shoppable lookbook missing from response.");
      }

      document.adoptNode(lookbook);

      const images = Array.from(lookbook.querySelectorAll(
        ".lookbook-product-image, .lookbook-media:first-child .lookbook-image"
      ));

      await Promise.all(images.map((image) => {
        image.loading = "eager";
        return image.decode();
      }));

      if (request !== this.#request || !this.isConnected) {
        return;
      }

      const restoreFocus = this.contains(document.activeElement);
      this.replaceWith(lookbook);

      if (restoreFocus) {
        lookbook.elements.swatches[index]?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (request === this.#request) {
        console.error(error);
      }
    } finally {
      if (request === this.#request) {
        this.removeAttribute("aria-busy");
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request++;
  }
}

customElements.define("component-shoppable-lookbook", ShoppableLookbook);
